/**
 * Additional Income Form Section
 * Manages non-employment income sources
 */

import * as React from "react";
import { DomainIncomeType } from "@porchpass/api-client";
import {
  Add01Icon,
  AddMoneyPlusIcon,
  AdditionalIncomeFields,
  type AdditionalIncomeFieldsVariant,
  Button,
  Delete03Icon,
  FormTable,
  FormTableAddButton,
  FormTableBody,
  useContainerRadiusSync,
  useFormTableAddButtonState,
} from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type { AdditionalIncome } from "../types/applicationTypes";
import {
  validateAdditionalIncomeField,
  validateDependentsAges,
} from "../utils/validationSchemas";

interface AdditionalIncomeFormProps {
  isCoBorrower?: boolean;
}

const createEmptyIncome = (): AdditionalIncome => ({
  id: crypto.randomUUID(),
  type: "" as DomainIncomeType,
  monthlyAmount: undefined,
});

function isChildSupportType(type: string): boolean {
  return (
    type === DomainIncomeType.IncomeChildSupport ||
    type === DomainIncomeType.IncomeSSIForDependents
  );
}

function getIncomeVariant(type: string): AdditionalIncomeFieldsVariant {
  if (type === DomainIncomeType.IncomeChildSupport) return "childSupport";
  if (type === DomainIncomeType.IncomeSSIForDependents) return "ssiDependents";
  return "generic";
}

export const AdditionalIncomeForm: React.FC<AdditionalIncomeFormProps> = ({
  isCoBorrower = false,
}) => {
  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const borrowerType = isCoBorrower ? "co" : "primary";
  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const [incomeForms, setIncomeForms] = React.useState<AdditionalIncome[]>([]);

  const incomes = borrower?.additionalIncome || [];

  React.useEffect(() => {
    if (incomes.length > 0 && incomeForms.length === 0) {
      setIncomeForms([...incomes]);
    }
  }, [incomes.length]);

  if (!borrower) return null;

  const getErrors = (index: number): Record<string, string> => {
    const prefix = `${borrowerPrefix}.additionalIncome.${index}.`;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(prefix)) {
        result[k.slice(prefix.length)] = v;
      }
    }
    return result;
  };

  const applyIncomeUpdate = (
    index: number,
    updates: Partial<AdditionalIncome>,
  ) => {
    const current = incomeForms[index];
    const updated = { ...current, ...updates };
    const newForms = [...incomeForms];
    newForms[index] = updated;
    setIncomeForms(newForms);
    store.updateAdditionalIncome(borrowerType, index, updated);
  };

  const handleAddIncome = () => {
    const newIncome = createEmptyIncome();
    store.addAdditionalIncome(borrowerType, newIncome);
    setIncomeForms([...incomeForms, newIncome]);
  };

  const updateIncomeField = <K extends keyof AdditionalIncome>(
    index: number,
    field: K,
    value: AdditionalIncome[K],
  ) => {
    if (typeof value === "string" || typeof value === "number") {
      const errorKey = `${borrowerPrefix}.additionalIncome.${index}.${String(field)}`;
      if (
        store.fieldErrors[errorKey] &&
        !validateAdditionalIncomeField(String(field), String(value))
      ) {
        store.clearFieldError(errorKey);
      }
    }
    if (field === "childrenAges" && Array.isArray(value)) {
      const errorKey = `${borrowerPrefix}.additionalIncome.${index}.childrenAges`;
      if (store.fieldErrors[errorKey] && !validateDependentsAges(value)) {
        store.clearFieldError(errorKey);
      }
    }
    applyIncomeUpdate(index, { [field]: value });
  };

  const handleBulkChange = (
    index: number,
    updates: Partial<AdditionalIncome>,
  ) => {
    const errPrefix = `${borrowerPrefix}.additionalIncome.${index}`;
    const current = incomeForms[index];
    const newType = updates.type ?? current.type;
    const toChildSupport = isChildSupportType(newType);
    const fromChildSupport = isChildSupportType(current.type);

    if (updates.type && store.fieldErrors[`${errPrefix}.type`]) {
      store.clearFieldError(`${errPrefix}.type`);
    }

    if (toChildSupport) {
      ["startMonth", "startYear", "endMonth", "endYear"].forEach((f) =>
        store.clearFieldError(`${errPrefix}.${f}`),
      );
    } else if (fromChildSupport) {
      store.clearFieldError(`${errPrefix}.childrenAges`);
    }

    applyIncomeUpdate(index, updates);
  };

  const handleDeleteIncome = (index: number) => {
    store.removeAdditionalIncome(borrowerType, index);
    setIncomeForms(incomeForms.filter((_, i) => i !== index));
  };

  const renderIncomeForm = (income: AdditionalIncome, index: number) => (
    <FormTableBody key={income.id ?? index}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-base font-medium text-[#111827]">Income Details</p>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => handleDeleteIncome(index)}
        >
          <Delete03Icon size={16} className="text-red-500" />
        </Button>
      </div>

      <AdditionalIncomeFields
        income={income}
        variant={getIncomeVariant(income.type)}
        showTypeSelect
        showTypeOther={income.type === DomainIncomeType.IncomeOther}
        errors={getErrors(index)}
        onFieldChange={(field, value) => updateIncomeField(index, field, value)}
        onBulkChange={(updates) => handleBulkChange(index, updates)}
      />
    </FormTableBody>
  );

  return (
    <FormTable
      title="Additional Income"
      headerIcon={
        <AddMoneyPlusIcon
          variant="bulk-rounded"
          size={16}
          className="text-[#BC3C7C]"
        />
      }
      style={containerStyles.style}
    >
      {incomeForms.map((income, index) => renderIncomeForm(income, index))}

      <FormTableAddButton
        onClick={handleAddIncome}
        {...addButtonState.interactionProps}
      >
        <Add01Icon variant="stroke-rounded" size={12} />
        {incomeForms.length === 0 ? "Add income" : "Additional income"}
      </FormTableAddButton>
    </FormTable>
  );
};

export default AdditionalIncomeForm;
